import { forwardRef, useImperativeHandle } from 'react';

import { useIntl } from 'react-intl';

import { Col, Form, Input, Radio, Row, Select, Spin } from 'antd';

import BoxContainer from '@src/components/BoxContainer';
import Title from '@src/components/BoxContainer/components/Title';
import { KeyValueWidget } from '@src/components/KeyValueWidget';
import { FORM_LAYOUT_DEFAULT, SCHEDULE_TYPES } from '@src/constants/commons';
import { ScheduleTemplateSelector } from '@src/containers/ScheduleTemplateSelector';
import SchedulesSelector from '@src/packages/dashboard/screens/SchedulesDashboard/components/ScheduleSelector';
import IntlMessages from '@src/util/IntlMessages';

import { FilterData } from '../../components/Shared/FilterData';
import UsersTypeSelector from '../../components/Shared/UsersTypeSelector';
import {
  DASHBOARDS_FORMS,
  DIVISIONS_TYPE,
  EXPRESSION_VALUE_TYPE,
  EXPRESSION_VALUE_TYPE_DEFAULT,
  TEMPLATE_IDS_DEFAULT,
} from '../../constants';
import useCounterForm from './hooks/useCounterForm';
import PercentageColorRules from './PercentageColorRules';

const CounterForm = forwardRef(({ chartId, divId, boardId }, formRef) => {
  const intl = useIntl();

  const {
    type,
    selectedSchedules,
    setSelectedSchedules,
    loading,
    setLoadingSchedules,
    initialScheduleIds,
    fetchedChartData,
    orgId,
    internalFormRef,
    valueType,
    watchDivisionFilter,
    watchDivisionIds,
    divisionsOptions,
    groupOptions,
    groupsLoading,
    handlePopupScrollGroups,
    buildBody,
    resetSelectedSchedules,
    getImperativeHandle,
    resetTemplateIds,
    resetUsersAndGroups,
    watchUsersType
  } = useCounterForm({ chartId, divId, boardId });

  useImperativeHandle(formRef, getImperativeHandle, [getImperativeHandle]);

  return (
    <Spin spinning={loading}>
      <Form
        onFieldsChange={buildBody}
        layout={FORM_LAYOUT_DEFAULT}
        ref={internalFormRef}
        name={DASHBOARDS_FORMS.SCHEDULE_COMPLIANCE_SUMMARY}
        initialValues={{
          id: chartId ?? null,
          title: '',
          description: '',
          userIds: [],
          groupIds: [],
          schedulesIds: [],
          type: null,
          valueType: EXPRESSION_VALUE_TYPE_DEFAULT,
          counterFormData: {},
          divisionFilter: DIVISIONS_TYPE.ALL,
          divisionIds: [],
          templateIds: TEMPLATE_IDS_DEFAULT,
        }}
      >
        <BoxContainer content>
          <Row gutter={16}>
            <Col span={24}>
              <Title.LabelForm
                required
                className="gx-guarnic-pb-1"
                value={intl.formatMessage({
                  id: 'dashboards.chart.form.title',
                })}
              />
              <Form.Item
                rules={[
                  {
                    required: true,
                    message: intl.formatMessage({
                      id: 'form.required',
                    }),
                  },
                ]}
                name="title"
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Title.LabelForm
                className="gx-guarnic-pb-1"
                value={intl.formatMessage({
                  id: 'dashboards.chart.form.description',
                })}
              />
              <Form.Item name="description">
                <Input.TextArea showCount rows={4} maxLength={250} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Title.SubTitle
                className="gx-pb-3"
                value={intl.formatMessage({
                  id: 'dashboards.chart.form.counter.filter.by',
                })}
              />
            </Col>
            <Form.Item noStyle hidden={!!divId}>
              <Col span={24}>
                <KeyValueWidget
                  label={
                    <Title.LabelForm
                      required
                      value={intl.formatMessage({
                        id: 'dashboards.chart.form.scheduleCompliance.filter.division.label',
                      })}
                    />
                  }
                  value={
                    <>
                      <Form.Item name="divisionFilter" className="gx-m-0">
                        <Radio.Group>
                          <Radio value={DIVISIONS_TYPE.ALL}>
                            <IntlMessages id="divisionSelector.allDivisions" />
                          </Radio>
                          <Radio value={DIVISIONS_TYPE.DIVISIONS}>
                            <IntlMessages id="sidebar.configuration.divisions" />
                          </Radio>
                        </Radio.Group>
                      </Form.Item>
                      {watchDivisionFilter === DIVISIONS_TYPE.DIVISIONS && (
                        <KeyValueWidget
                          label={
                            <Title.LabelForm
                              className="gx-mb-2"
                              required
                              value={intl.formatMessage({
                                id: 'dashboards.chart.form.scheduleCompliance.filter.division.select.label',
                              })}
                            />
                          }
                          value={
                            <Form.Item name="divisionIds">
                              <Select
                                allowClear
                                mode="multiple"
                                options={divisionsOptions}
                                onChange={() => {
                                  resetSelectedSchedules();
                                  resetTemplateIds();
                                }}
                              />
                            </Form.Item>
                          }
                        />
                      )}
                    </>
                  }
                />
              </Col>
            </Form.Item>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <KeyValueWidget
                label={
                  <Title.LabelForm
                    className="gx-mb-2"
                    required
                    value={intl.formatMessage({
                      id: 'dashboards.chart.form.counter.filter.users.label',
                    })}
                  />
                }
                value={
                  <UsersTypeSelector
                    usersType={watchUsersType}
                    groupOptions={groupOptions}
                    groupsLoading={groupsLoading}
                    handlePopupScrollGroups={handlePopupScrollGroups}
                    onReset={resetUsersAndGroups}
                    divisionIds={divId ? [divId] : watchDivisionIds}
                    orgId={orgId}
                    userIds={fetchedChartData?.queryParams?.userIds || []}
                  />
                }
              />
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <KeyValueWidget
                label={
                  <Title.LabelForm
                    required
                    className="gx-mb-2"
                    value={intl.formatMessage({
                      id: 'dashboards.chart.form.counter.filter.information.label',
                    })}
                  />
                }
                value={
                  <>
                    <Form.Item name="type" noStyle>
                      <FilterData onChange={() => resetSelectedSchedules()} className="gx-mb-2" />
                    </Form.Item>
                    {type !== null && type !== SCHEDULE_TYPES.task && (
                      <Col span={24}>
                        <Form.Item name="schedulesIds">
                          <SchedulesSelector
                            selectedSchedules={selectedSchedules}
                            type={type || SCHEDULE_TYPES.task}
                            setSelected={setSelectedSchedules}
                            setLoading={setLoadingSchedules}
                            divisionIds={divId ? [divId] : watchDivisionIds}
                            initialScheduleIds={initialScheduleIds}
                            exactMatch={true}
                          />
                        </Form.Item>
                      </Col>
                    )}
                    {type === SCHEDULE_TYPES.task && (
                      <Col span={24}>
                        <Form.Item name="templateIds">
                          <ScheduleTemplateSelector
                            organizationId={orgId}
                            divisionIds={divId ? [divId] : watchDivisionIds}
                            templateIds={fetchedChartData?.queryParams?.templateIds || []}
                          />
                        </Form.Item>
                      </Col>
                    )}
                  </>
                }
              />
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Col span={24}>
                <Title.SubTitle
                  className="gx-pb-3"
                  value={intl.formatMessage({
                    id: 'dashboards.chart.form.counter.result.format',
                  })}
                />
              </Col>
              <Col span={24}>
                <Title.LabelForm
                  required
                  value={intl.formatMessage({
                    id: 'dashboards.chart.form.counter.result.format.description',
                  })}
                />
              </Col>
              <Col span={24}>
                <Form.Item
                  name="valueType"
                  rules={[{ required: true, message: intl.formatMessage({ id: 'form.required' }) }]}
                >
                  <Radio.Group>
                    <Radio value={EXPRESSION_VALUE_TYPE.PERCENTAGE}>
                      {intl.formatMessage({
                        id: 'dashboards.chart.form.counter.result.format.percentage',
                      })}
                    </Radio>
                    <Radio value={EXPRESSION_VALUE_TYPE.NUMBER}>
                      <IntlMessages id="eventTypes.component.number" />
                    </Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              {valueType === EXPRESSION_VALUE_TYPE.PERCENTAGE && (
                <Col span={24}>
                  <Title.LabelForm
                    value={intl.formatMessage({
                      id: 'dashboards.chart.form.counter.percentage.rules.title',
                    })}
                  />
                  <Title.Description
                    value={intl.formatMessage({
                      id: 'dashboards.chart.form.counter.percentage.rules.description',
                    })}
                  />
                  <Form.Item name="percentageConfig">
                    <PercentageColorRules />
                  </Form.Item>
                </Col>
              )}
            </Col>
          </Row>
        </BoxContainer>
        <Form.Item name="counterFormData" hidden />
        <Form.Item name="id" hidden />
      </Form>
    </Spin>
  );
});

export default CounterForm;
